
	import { Component, ProvideReactive, Vue, Watch } from 'vue-property-decorator';
	import { Observer } from 'mobx-vue';
	import HelpModal from '@/views/prescription/components/HelpModal.vue';
	import { diagnoses } from '@/store/prescriptionStore';
	import DualButtonModal from '@/components/modals/DualButtonModal.vue';
	import errorMessages from '@/assets/error-messages.json';
	import PrescriptionForm from '@/components/new-prescription/PrescriptionForm.vue';
	import orthoticStylesArray from '@/store/Products';

	@Observer
	@Component({
		components: { DualButtonModal, HelpModal, PrescriptionForm }
	})
	export default class SavedRx extends Vue {
		@ProvideReactive() hamburgerView = false;
		private reg = new RegExp('[^\\w\\-\\. ]', 'gi');
		private diagnosesList = diagnoses;
		private diagnosisInfo: boolean = false;
		private diagnosisProp = {
			title: 'Diagnosis',
			display: 'Please provide a diagnosis with all orders.',
			description:
				'Our order review process is much better when we know your diagnosis. Additionally, if you select "Request Clinical Consultation", your diagnosis is critical for ensuring we make the right recommendations back to you.'
		};
		private action: 'Adding' | 'Modifying' = 'Adding';

		private initialTitle = '';
		private title = '';
		private companyID: string | undefined = undefined;
		private diagnosis = this.diagnosesList[0];

		private deleteConfirmation = false;

		@Watch('title', { immediate: true })
		onTitleChanged() {
			if (this.reg.test(this.title)) {
				this.$state.utils.pushMessage(errorMessages.snackbars.noSpecialCharsSavedRx);
				this.title = this.title.replaceAll(this.reg, '');
			}
		}

		async activated() {
			this.title = '';
			this.initialTitle = '';
			this.diagnosis = this.diagnosesList[0];
			const name = this.$route.query['name'];
			const companyID = this.$route.query['companyID'];

			if (name && !Array.isArray(name)) {
				this.action = 'Modifying';
				this.title = name;
				this.companyID = companyID as string | undefined;
				this.initialTitle = name;
				try {
					if (await this.checkRxAvailability(name)) {
						await this.fetchPrescription(name, companyID as string | undefined);
					} else {
						await this.back();
					}
				} catch (e) {
					this.$state.utils.pushMessage(`Prescription ${name} is corrupted`, true);
					await this.$router.push('saved-rx-list');
				}
			} else {
				this.action = 'Adding';
				this.$state._patient.patientUIData.notes = '';
			}
		}

		private async back() {
			await this.$router.push('saved-rx-list');
		}

		private checkIfTitleIsForbidden = (arg: string): boolean => {
			return arg in orthoticStylesArray;
		};

		private async save() {
			// If exists and adding or title differs from initial one
			if (
				(await this.checkRxAvailability(this.title)) &&
				(this.action === 'Adding' || this.checkIfTitleIsForbidden(this.title) || this.initialTitle !== this.title)
			) {
				if (this.initialTitle === this.title) {
					this.$state.utils.pushMessage(`Please give the new prescription name to save.`, true);
				} else {
					this.$state.utils.pushMessage(`Prescription with name '${this.title}' already exists!`, true);
				}
				return;
			}
			// If modifying existing one, title exists and initial title differs from current one
			if (
				this.action === 'Modifying' &&
				this.initialTitle !== '' &&
				!this.checkIfTitleIsForbidden(this.initialTitle) &&
				this.initialTitle !== this.title
			) {
				await this.$engine.savedRx.renameAndSaveRx(
					this.initialTitle,
					this.title,
					JSON.stringify({
						notes: this.$state._patient.patientUIData.notes,
						diagnosis: this.diagnosis,
						...this.$state.prescription.newPrescription
					})
				);
			} else {
				await this.$engine.savedRx.upsertSavedRx(
					this.title,
					JSON.stringify({
						notes: this.$state._patient.patientUIData.notes,
						diagnosis: this.diagnosis,
						...this.$state.prescription.newPrescription
					})
				);
			}
			await this.$router.push({ path: '/saved-rx-list', query: { prescription: this.title } });
		}

		private async checkRxAvailability(name: string) {
			return !!(await this.$engine.savedRx.isRxAvailable(name));
		}

		private async fetchPrescription(prescriptionName: string, companyID: string | undefined = undefined) {
			const prescriptionWithDiagnosis = await this.$engine.savedRx.getRx(prescriptionName, companyID);
			if (prescriptionWithDiagnosis?.fileData) {
				const { notes, diagnosis, ...prescription } = JSON.parse(prescriptionWithDiagnosis.fileData);
				this.$state.prescription.newPrescription = prescription;
				this.diagnosis = diagnosis || this.diagnosesList[0];
				if (notes) {
					this.$state._patient.patientUIData.notes = notes;
				} else {
					this.$state._patient.patientUIData.notes = '';
				}
			}
		}

		public closeModal() {
			this.deleteConfirmation = false;
		}

		public async deleteRx() {
			await this.$state.engine.savedRx.deleteRx(this.initialTitle);
			this.closeModal();
			await this.$router.push('saved-rx-list');
		}
	}
